import { useEffect, useState } from 'react';

import { Alert, Button, FormField, Input, RadioCardGroup } from '@playerbase/ui';
import {
  StudioSetupFormSchema,
  StudioRpcResponseSchema,
  TeamSizeSchema,
  UseCaseSchema,
  TEAM_SIZE_LABELS,
  USE_CASE_LABELS,
} from '@playerbase/types';

import { supabase } from '../../lib/supabase.js';
import { useAuth } from '../../auth/use-auth.js';
import { useShell } from '../../shell/use-shell.js';
import { studioRpcErrorMessage } from '../../studios/studio-setup-form.js';

const TEAM_SIZE_OPTIONS = TeamSizeSchema.options.map((value) => ({
  value,
  label: TEAM_SIZE_LABELS[value],
}));

const USE_CASE_OPTIONS = UseCaseSchema.options.map((value) => ({
  value,
  label: USE_CASE_LABELS[value],
}));

export function StudioInformationPanel(): JSX.Element {
  const { activeStudio } = useShell();
  const { refreshMemberships } = useAuth();

  const [name, setName] = useState(activeStudio.name);
  const [teamSize, setTeamSize] = useState<string>(activeStudio.teamSize);
  const [useCase, setUseCase] = useState<string>(activeStudio.useCase);
  const [saving, setSaving] = useState(false);
  const [rpcError, setRpcError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Reset local form state when the active studio changes (switcher).
  useEffect(() => {
    setName(activeStudio.name);
    setTeamSize(activeStudio.teamSize);
    setUseCase(activeStudio.useCase);
    setRpcError('');
    setSavedAt(null);
  }, [activeStudio.id, activeStudio.name, activeStudio.teamSize, activeStudio.useCase]);

  const trimmedName = name.trim();
  const dirty =
    trimmedName !== activeStudio.name ||
    teamSize !== activeStudio.teamSize ||
    useCase !== activeStudio.useCase;

  const isValid = StudioSetupFormSchema.safeParse({ name, teamSize, useCase }).success;

  async function handleSave(): Promise<void> {
    setRpcError('');

    const parsed = StudioSetupFormSchema.safeParse({ name, teamSize, useCase });
    if (!parsed.success) return;

    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('update_studio_info', {
        p_studio_id: activeStudio.id,
        p_name: parsed.data.name.trim(),
        p_team_size: parsed.data.teamSize,
        p_use_case: parsed.data.useCase,
      });

      if (error) {
        setRpcError(studioRpcErrorMessage(error.message));
        return;
      }

      const response = StudioRpcResponseSchema.safeParse(data);
      if (!response.success) {
        setRpcError('Studio updated but the response was malformed. Please refresh the page.');
        return;
      }

      // Refresh auth memberships so the sidebar/switcher pick up the new name
      // (the active studio object is sourced from auth context).
      await refreshMemberships();
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
      <div>
        <h2 className="font-display text-xl font-bold text-text">Studio information</h2>
        <p className="mt-1 text-sm text-text-muted">
          The basics about {activeStudio.name}. Changing the name will not change
          the studio's URL.
        </p>
      </div>

      {rpcError !== '' && <Alert tone="danger">{rpcError}</Alert>}

      <div className="flex flex-col gap-6">
        <FormField>
          <FormField.Label>Studio name</FormField.Label>
          <FormField.Control>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="w-full"
            />
          </FormField.Control>
        </FormField>

        <FormField>
          <FormField.Label>Team size</FormField.Label>
          <FormField.Control>
            <RadioCardGroup
              name="studio-info-team-size"
              value={teamSize}
              onChange={setTeamSize}
              options={TEAM_SIZE_OPTIONS}
              orientation="grid"
            />
          </FormField.Control>
        </FormField>

        <FormField>
          <FormField.Label>How will you use PlayerBase?</FormField.Label>
          <FormField.Control>
            <RadioCardGroup
              name="studio-info-use-case"
              value={useCase}
              onChange={setUseCase}
              options={USE_CASE_OPTIONS}
              orientation="vertical"
            />
          </FormField.Control>
        </FormField>

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="md"
            disabled={!dirty || !isValid || saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          {dirty && !saving && (
            <span className="text-xs text-text-muted">Unsaved changes</span>
          )}
          {!dirty && savedAt !== null && (
            <span className="text-xs text-text-muted">Saved.</span>
          )}
        </div>
      </div>
    </div>
  );
}
